import React from 'react';
import { Box } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { Meter } from './home/ui';

/** Read by screen readers, not shown. In px: in sx a bare 1 means 100%. */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export interface StepItem {
  key: string;
  title: string;
  hint?: React.ReactNode;
  done: boolean;
  /** The step to do now: drawn in the accent and marked for screen readers. */
  current?: boolean;
  /** The buttons that do it; hidden once the step is done. */
  actions?: React.ReactNode;
}

interface Props {
  /** The heading, and the name of the section. */
  title: string;
  steps: StepItem[];
  /** Beside the count, such as a link that hides the list. */
  aside?: React.ReactNode;
  /** A few words under the heading, before the steps. */
  intro?: React.ReactNode;
}

/**
 * A checklist card: a heading with how many steps are done, a progress bar
 * and the steps, each with its way to do it until it is done. "Primeiros
 * passos" on the personal Início and "Monte o grupo" on a new group.
 */
export const StepList: React.FC<Props> = ({ title, steps, aside, intro }) => {
  const { t } = useTranslation('dashboard');
  const done = steps.filter((step) => step.done).length;
  const total = steps.length;

  return (
    <Box
      component='section'
      aria-label={title}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, p: { xs: 2.25, md: 2.75 }, borderRadius: `${tokens.radius.card}px`, border: `1px solid ${tokens.line}`, bgcolor: tokens.surface }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box component='h2' sx={{ m: 0, fontFamily: tokens.display, fontSize: 19, fontWeight: 700 }}>{title}</Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Box component='span' sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink2 }}>{t('firstSteps.progress', { done, total })}</Box>
          {aside}
        </Box>
      </Box>
      {intro && <Box component='p' sx={{ m: 0, fontSize: 14.5, color: tokens.ink2, lineHeight: 1.5, maxWidth: 680 }}>{intro}</Box>}
      <Meter ratio={total ? done / total : 0} track={tokens.line} />

      <Box component='ol' sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column' }}>
        {steps.map((step, index) => {
          const isDone = step.done;
          const isNext = Boolean(step.current) && !isDone;
          return (
            <Box
              component='li'
              key={step.key}
              aria-current={isNext ? 'step' : undefined}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 1.5, borderTop: index ? `1px solid ${tokens.line}` : 'none' }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  bgcolor: isDone ? tokens.accent : isNext ? tokens.accentSoft : tokens.bg,
                  color: isDone ? '#FFFFFF' : isNext ? tokens.accent : tokens.ink3,
                }}
              >
                {isDone ? <CheckIcon sx={{ fontSize: 17 }} /> : index + 1}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                <Box component='strong' sx={{ color: isDone ? tokens.ink3 : tokens.ink, textDecoration: isDone ? 'line-through' : 'none' }}>
                  {step.title}
                  {isDone && <Box component='span' sx={visuallyHidden}>{` (${t('firstSteps.done')})`}</Box>}
                </Box>
                {!isDone && step.hint && (
                  <Box component='span' sx={{ fontSize: 13.5, color: tokens.ink2, lineHeight: 1.45 }}>{step.hint}</Box>
                )}
                {!isDone && step.actions && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>{step.actions}</Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
